"use client"

// [VV11-TEARDOWN] lp-no-discount-framing A/B test (DEV-10019) — delete this
// file at teardown. See the checklist in
// @local/tracking/src/experiments/noDiscountFraming.
import { useEffect } from "react"
import { trackAction } from "@local/tracking/src/trackActions"
import {
  registerPostHogProperties,
  trackPostHog,
} from "@local/tracking/src/scripts/posthog"
import {
  VV11_COOKIE,
  VV11_FEATURE_FLAG,
  isVv11Variant,
  writeVv11Fallback,
  type Vv11Variant,
} from "@local/tracking/src/experiments/noDiscountFraming"
import { getRecoveryStorage } from "@local/tracking/src/recover/getRecoveryStorage"

// Manual capture bypasses PostHog's built-in per-session dedup, so gate
// re-fires across remounts with a module-level Set (same pattern as the vv5 /
// apm exposure beacons).
const firedVv11Exposure = new Set<string>()

/**
 * Signals lp-no-discount-framing exposure.
 *
 * The brief's primary metric is `purchase ÷ LP exposure` — "because the
 * discount banner sits at the top of the LP and is seen by every visitor, the
 * LP view IS the exposure" — so this fires on the LP itself, not the checkout.
 * That is the whole point of the metric, and it also means LP→checkout
 * clickthrough IS measurable for this experiment (unlike vv10, whose
 * checkout-only exposure forces its checkout rate to ~100%).
 *
 * Middleware resolves the arm server-side and the assignment arrives as a prop
 * from the block builder; without this call the experiment shows zero enrolled
 * users (PostHog needs the `$feature_flag_called` event to count exposure).
 * Mounted for BOTH arms, but only when middleware resolved a variant (i.e. on
 * the baerskin-hoodie-4 LP), so exposure is restricted to eligible traffic.
 */
export const Vv11ExposureBeacon = ({ variant }: { variant: Vv11Variant }) => {
  useEffect(() => {
    if (!isVv11Variant(variant)) {
      return
    }

    // Mirror the arm into sessionStorage so the static checkout can still find
    // it if the cookie does not survive (see writeVv11Fallback). Deliberately
    // BEFORE the override guard below: that guard is about not polluting
    // EXPOSURE, whereas this is about the shopper seeing a coherent page — a
    // forced arm must reach the checkout too. Separate concern from this
    // component's name; it lives here because this is the one client component
    // already mounted on the LP for both arms with the server-resolved variant.
    writeVv11Fallback(variant)

    // Don't enroll QA / stakeholder-review sessions driven by a VALID `?vv11=`
    // override. Unlike vv5 this check is NOT gated on STAGE: the vv11 override
    // is honoured on production too (forcing an arm changes only copy, never
    // the price charged), so an override-carrying prd visitor was forced rather
    // than assigned and must not count. A bogus value (e.g. `?vv11=xyz`) is
    // ignored by middleware and falls through to a real assignment, so those
    // visitors MUST still be counted.
    if (
      typeof window !== "undefined" &&
      isVv11Variant(
        new URLSearchParams(window.location.search).get(VV11_COOKIE)
      )
    ) {
      return
    }

    if (firedVv11Exposure.has(variant)) return
    firedVv11Exposure.add(variant)

    // The campaign this pageview arrived on, captured AT EXPOSURE.
    //
    // Assignment is a plain 60/40 hash split, so this is NOT used to decide the
    // arm — it is kept because Rafael asked for it (PR #4036) and it is what
    // lets a readout break results down by ad campaign. That matters for the
    // deferred question of whether the no-discount creative ends up running as
    // its own campaign against a separate audience, which would reintroduce a
    // confound this split cannot see.
    //
    // Landing-hit-only: a returning visitor records `null` here. That is fine —
    // PostHog is person-based, so the campaign captured at a person's FIRST
    // exposure stays on the person and their later events remain segmentable.
    const utmCampaign =
      typeof window !== "undefined"
        ? new URLSearchParams(window.location.search).get("utm_campaign")
        : null

    // PostHog convention: tags every subsequent event with the variant so
    // downstream conversions attribute to the experiment, not just exposure.
    // `utm_campaign` rides along so results can be broken down by campaign in
    // PostHog too — `trackAction` already stamps it on datalake events, but
    // nothing carried it onto the manually-captured `$feature_flag_called`.
    registerPostHogProperties({
      [`$feature/${VV11_FEATURE_FLAG}`]: variant,
    })
    trackPostHog("$feature_flag_called", {
      $feature_flag: VV11_FEATURE_FLAG,
      $feature_flag_response: variant,
      utm_campaign: utmCampaign,
    })

    const { geo, loc } = getRecoveryStorage()
    trackAction("split_test", {
      experiment: VV11_FEATURE_FLAG,
      variant,
      region: geo?.region ?? loc?.regionName ?? null,
      // Explicit rather than relying on the global `utm_campaign` field in
      // trackActions.tsx, so the exposure row carries it unambiguously.
      utm_campaign: utmCampaign,
    })
  }, [variant])

  return null
}
